import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { FilmCard } from '../components/FilmCard';
import { useAuth } from '../contexts/AuthContext';
import { useFilm, useSimilarFilms } from '../hooks/useFilms';
import { Heart, Bookmark, ThumbsDown, Clock, Calendar, Film as FilmIcon, MapPin, Play, Loader2 } from 'lucide-react';
export function FilmDetails() {
  const {
    id
  } = useParams();
  const {
    isLoggedIn
  } = useAuth();
  const {
    film,
    loading
  } = useFilm(id);
  const {
    films: similarFilms,
    loading: similarLoading
  } = useSimilarFilms(film);
  if (loading) {
    return <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center" data-id="element-356">
        <Loader2 className="w-12 h-12 text-[#D22B2B] animate-spin mb-6" data-id="element-357" />
        <p className="text-gray-500 font-serif text-sm uppercase tracking-widest" data-id="element-358">
          Загрузка фильма...
        </p>
      </div>;
  }
  if (!film) {
    return <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center" data-id="element-359">
        <FilmIcon className="w-16 h-16 text-gray-700 mb-6" data-id="element-360" />
        <h1 className="font-noir text-4xl text-white mb-4" data-id="element-361">Фильм не найден</h1>
        <p className="text-gray-500 font-serif mb-8" data-id="element-362">
          Запрошенный фильм отсутствует в каталоге
        </p>
        <Link to="/catalog" data-id="element-363">
          <Button variant="primary" data-id="element-364">Перейти в каталог</Button>
        </Link>
      </div>;
  }
  return <div className="min-h-screen bg-[#0a0a0a] pb-20" data-id="element-365">
      {/* Top Banner / Breadcrumbs */}
      <div className="relative bg-[#050505] text-gray-500 py-4 text-[10px] uppercase tracking-[0.2em] border-b border-[#222]" data-id="element-366">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,transparent_0%,rgba(0,0,0,0.3)_100%)] pointer-events-none" data-id="element-367"></div>
        <div className="container mx-auto px-4 relative z-10" data-id="element-368">
          <Link to="/" className="hover:text-white transition-colors" data-id="element-369">
            Главная
          </Link>{' '}
          /
          <Link to="/catalog" className="hover:text-white mx-2 transition-colors" data-id="element-370">
            Каталог
          </Link>{' '}
          /<span className="text-[#D22B2B] mx-2" data-id="element-371">{film.title}</span>
        </div>
      </div>

      {/* Content area */}
      <div className="container mx-auto px-4 py-12" data-id="element-372">
        <div className="grid grid-rows lg:grid-cols-12 gap-12" data-id="element-373">
          {/* Left Column: Poster & Actions */}
          <div className="lg:col-span-4 space-y-8" data-id="element-374">
            <div className="relative border border-[#333] shadow-[0_0_50px_rgba(0,0,0,0.8)] bg-[#141414] group overflow-hidden" data-id="element-375">
              <img src={film.posterUrl} alt={film.title} className="w-full h-auto block opacity-90 group-hover:opacity-100 transition-opacity duration-500" data-id="element-376" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" data-id="element-377"></div>

              <div className="absolute top-4 right-4 bg-[#D22B2B] text-white px-3 py-1 font-serif text-xs tracking-widest shadow-[0_0_15px_rgba(210,43,43,0.4)]" data-id="element-378">
                {film.ageCategory}
              </div>

              <div className="absolute bottom-6 left-0 right-0 flex justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0" data-id="element-379">
                <Button variant="primary" className="rounded-full px-8 shadow-[0_0_20px_rgba(210,43,43,0.4)]" data-id="element-380">
                  <Play className="w-4 h-4 mr-2 fill-current" data-id="element-381" />
                  Смотреть
                </Button>
              </div>
            </div>

            {/* Stills Carousel */}
            {film.stillUrls && film.stillUrls.length > 0 && <div className="grid grid-cols-3 gap-3" data-id="element-382">
                {film.stillUrls.map((url, idx) => <div key={idx} className="aspect-video border border-[#333] overflow-hidden cursor-pointer hover:border-[#D22B2B] transition-colors group" data-id="element-383">
                    <img src={url} alt="Кадр из фильма" className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity duration-300" data-id="element-384" />
                  </div>)}
              </div>}

            {isLoggedIn && <div className="grid grid-cols-3 gap-3" data-id="element-385">
                <Button variant="outline" className="flex flex-col h-auto py-4 gap-2 text-[10px] border-[#333] hover:border-[#D22B2B] hover:text-[#D22B2B]" data-id="element-386">
                  <Heart className="w-5 h-5" data-id="element-387" />
                  Любимое
                </Button>
                <Button variant="outline" className="flex flex-col h-auto py-4 gap-2 text-[10px] border-[#333] hover:border-[#D22B2B] hover:text-[#D22B2B]" data-id="element-388">
                  <Bookmark className="w-5 h-5" data-id="element-389" />
                  Буду
                </Button>
                <Button variant="outline" className="flex flex-col h-auto py-4 gap-2 text-[10px] border-[#333] hover:border-gray-500" data-id="element-390">
                  <ThumbsDown className="w-5 h-5" data-id="element-391" />
                  Не мое
                </Button>
              </div>}
          </div>

          {/* Right Column: Info */}
          <div className="lg:col-span-8" data-id="element-392">
            <div className="mb-10 border-b border-[#222] pb-10" data-id="element-393">
              <div className="flex flex-wrap items-center gap-3 mb-6" data-id="element-394">
                <Badge variant="red" className="text-[10px] py-1 px-3" data-id="element-395">
                  № {film.catalogNumber}
                </Badge>
                {film.genreTags.map(g => <Badge key={g} variant="outline" className="text-[10px] py-1 px-3 border-[#444] text-gray-300" data-id="element-396">
                    {g}
                  </Badge>)}
              </div>

              <h1 className="font-noir text-5xl md:text-7xl font-medium text-white mb-4 leading-none tracking-tight" data-id="element-397">
                {film.title}
              </h1>
              <p className="text-2xl md:text-3xl text-[#D22B2B] font-serif italic mb-8 opacity-80" data-id="element-398">
                {film.year} год
              </p>

              <div className="bg-[#141414] p-8 border-l-2 border-[#D22B2B] relative shadow-2xl" data-id="element-399">
                <p className="text-lg leading-relaxed text-gray-300 font-serif font-light" data-id="element-400">
                  {film.descriptionFull}
                </p>
                <div className="mt-6 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D22B2B] flex items-center gap-3 opacity-70" data-id="element-401">
                  <div className="w-1 h-1 bg-[#D22B2B] rounded-full shadow-[0_0_5px_#D22B2B]" data-id="element-402"></div>
                  Описание из официального каталога
                </div>
              </div>
            </div>

            <div className="space-y-10 mb-16" data-id="element-403">
              {/* Production Details — horizontal row */}
              <div className="border-b border-[#222] pb-8" data-id="element-404">
                <h3 className="font-serif text-xs font-bold uppercase tracking-[0.2em] text-gray-500 mb-6 border-b border-[#333] pb-2 inline-block" data-id="element-405">
                  Детали производства
                </h3>
                <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm font-light" data-id="element-406">
                  <div className="flex items-center gap-2" data-id="element-407">
                    <FilmIcon className="w-4 h-4 text-[#D22B2B]" data-id="element-408" />
                    <span className="text-gray-300" data-id="element-409">
                      {film.studio || 'Киностудия не указана'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-410">
                    <MapPin className="w-4 h-4 text-[#D22B2B]" data-id="element-411" />
                    <span className="text-gray-300" data-id="element-412">
                      {film.republic || 'Страна не указана'}
                    </span>
                  </div>
                  {film.duration && <div className="flex items-center gap-2" data-id="element-413">
                      <Clock className="w-4 h-4 text-[#D22B2B]" data-id="element-414" />
                      <span className="text-gray-300" data-id="element-415">
                        {film.duration} мин.
                        {film.meters ? ` / ${film.meters} м` : ''}
                      </span>
                    </div>}
                  {film.releaseDateVE && <div className="flex items-center gap-2" data-id="element-416">
                      <Calendar className="w-4 h-4 text-[#D22B2B]" data-id="element-417" />
                      <span className="text-gray-300" data-id="element-418">
                        Премьера: {film.releaseDateVE}
                      </span>
                    </div>}
                  {film.color && <div className="flex items-center gap-2" data-id="element-419">
                      <div className="w-4 h-4 rounded-full border border-gray-600 bg-gradient-to-br from-gray-800 to-black" data-id="element-420"></div>
                      <span className="text-gray-300" data-id="element-421">{film.color}</span>
                    </div>}
                </div>
              </div>

              {/* Credits — full width below */}
              <div data-id="element-422">
                <h3 className="font-serif text-xs font-bold uppercase tracking-[0.2em] text-gray-500 mb-6 border-b border-[#333] pb-2 inline-block" data-id="element-423">
                  Съемочная группа
                </h3>
                <dl className="space-y-4 text-sm font-light" data-id="element-424">
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4" data-id="element-425">
                    <dt className="text-gray-500 font-serif shrink-0 w-24" data-id="element-426">
                      Режиссёр
                    </dt>
                    <dd className="text-gray-200" data-id="element-427">{film.director}</dd>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4" data-id="element-428">
                    <dt className="text-gray-500 font-serif shrink-0 w-24" data-id="element-429">
                      Сценарий
                    </dt>
                    <dd className="text-gray-200" data-id="element-430">
                      {Array.isArray(film.writers) ? film.writers.join(', ') : film.writers}
                    </dd>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4" data-id="element-431">
                    <dt className="text-gray-500 font-serif shrink-0 w-24" data-id="element-432">
                      В ролях
                    </dt>
                    <dd className="text-gray-200 leading-relaxed" data-id="element-433">
                      {Array.isArray(film.cast) ? film.cast.join(', ') : film.cast}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Similar Films */}
            {similarLoading ? <div className="flex items-center justify-center py-12" data-id="element-434">
                <Loader2 className="w-6 h-6 text-[#D22B2B] animate-spin" data-id="element-435" />
              </div> : similarFilms.length > 0 ? <div data-id="element-436">
                <h3 className="font-noir text-3xl text-white mb-8 tracking-wide" data-id="element-437">
                  Похожие фильмы
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-438">
                  {similarFilms.map((f, idx) => <FilmCard key={f.id} film={f} index={idx} data-id="element-439" />)}
                </div>
              </div> : null}
          </div>
        </div>
      </div>
    </div>;
}